/**
 * TV-domain helpers shared across the TV pages.
 *
 * Mirrors `arr_tv::tba::is_tba_title` (Rust). Keep these two in sync
 * — issue #69.
 */

import type { SeriesType } from "@/api/types";

/**
 * Issue #65: human-readable label for a {@link SeriesType}. The wire
 * format is kebab-case (`"standard" | "daily" | "anime"`); the UI
 * mostly wants Title Case for chip labels and the dropdown options.
 * Centralised here so SeriesList, SeriesDetail and the bulk admin
 * modal stay in sync.
 */
export const SERIES_TYPE_LABEL: Record<SeriesType, string> = {
  standard: "Standard",
  daily: "Daily",
  anime: "Anime",
};

export const SERIES_TYPE_OPTIONS: ReadonlyArray<{
  value: SeriesType;
  label: string;
  hint: string;
}> = [
  {
    value: "standard",
    label: "Standard",
    hint: "Default. SxxEyy parser — most western shows.",
  },
  {
    value: "daily",
    label: "Daily",
    hint: "Date-based matching — talk shows, news, soaps.",
  },
  {
    value: "anime",
    label: "Anime",
    hint: "Absolute-numbered releases — `[Group] Show - 045`.",
  },
];

const EPISODE_N_RE = /^episode\s+\d+$/i;
const SXXEXX_RE = /^s\d+e\d+$/i;

/**
 * Returns true when an episode title is a TVDB / TMDB placeholder:
 * - null / undefined
 * - empty / whitespace-only
 * - case-insensitive `"TBA"` / `"TBD"`
 * - `Episode 15` (generic TVDB placeholder)
 * - `S01E15` (the SxxEyy tag used as a title)
 *
 * Single source of truth for "should the UI render `Title TBD` instead
 * of `TBA`". The Rust importer + filename renderer use the same
 * check.
 */
export function isTbaTitle(title: string | null | undefined): boolean {
  if (title == null) return true;
  const trimmed = title.trim();
  if (trimmed.length === 0) return true;
  const lower = trimmed.toLowerCase();
  if (lower === "tba" || lower === "tbd") return true;
  if (EPISODE_N_RE.test(trimmed)) return true;
  if (SXXEXX_RE.test(trimmed)) return true;
  return false;
}

/**
 * Episodes air at midnight UTC on `air_date`. Returns true once that
 * moment is reached (or for `null` air_date — we don't know, so the
 * UI surfaces it as wanted rather than hiding it).
 */
export function isAired(airDate: string | null | undefined): boolean {
  if (airDate == null) return true;
  const t = Date.parse(airDate);
  if (!Number.isFinite(t)) return true;
  return t <= Date.now();
}

/**
 * For episodes that haven't aired yet, render a short, muted label like
 * `Airs Apr 30` (within the next 14 days → relative phrasing) or
 * `Airs in 5 weeks` (further out). Returns `null` when the episode has
 * already aired or `air_date` is missing — callers should fall back to
 * the regular Search button in that case. Mirrors the air-date gate
 * `arr_tv::airdate::episode_search_allowed` (issue #64).
 */
export function airsLabel(airDate: string | null | undefined): string | null {
  if (airDate == null) return null;
  const t = Date.parse(airDate);
  if (!Number.isFinite(t)) return null;
  const now = Date.now();
  if (t <= now) return null;
  const deltaMs = t - now;
  const day = 24 * 60 * 60 * 1000;
  const days = Math.round(deltaMs / day);
  if (days <= 1) {
    const hours = Math.max(1, Math.round(deltaMs / (60 * 60 * 1000)));
    return hours < 24 ? `Airs in ${hours}h` : "Airs tomorrow";
  }
  if (days < 14) {
    return `Airs in ${days} days`;
  }
  // Further out: drop the relative count, surface the date instead.
  // `Apr 30` style — short month + day, no year (the ambient series
  // page makes the year obvious).
  const d = new Date(t);
  const monthShort = d.toLocaleDateString(undefined, { month: "short" });
  return `Airs ${monthShort} ${d.getDate()}`;
}
